import React, { Suspense, useState } from "react";

import { DashboardRouter } from "../../routes/DashboardRouter";
import { Sidebar } from "./components/sidebar/Sidebar";
import { SIDEBAR_BG } from "./components/sidebar/menuConfig";
import { Topbar } from "./components/topbar/Topbar";
import { useMediaQuery } from "react-responsive";
import { useDispatch, useSelector } from "react-redux";
import { setMenuView, setSmallScreen, setMenuState } from "../../actions/dbUiActions";
import { useEffect } from "react";
import { useAppSelector } from "../../hooks/useAppSelector";
import { collaboratorsStartInitialLoading } from "../../actions/collaboratorActions";
import { Box, CircularProgress, Drawer } from "@mui/material";
import { RootState } from "../../store/store";
import {
  attendanceRecordsThunks,
  collaboratorThunks,
  jobThunks,
} from "../../store/thunks";
import { branchThunks } from "../../store/thunks/branch.thunks";
import { EmploymentThunks } from "../../store/thunks/employment.thunks";
import { perfMark } from "../../utils/perf-tracker";
import { ChunkErrorBoundary } from "../../components/ChunkErrorBoundary";

export const DashboardLayout = () => {
  perfMark("dashboard-layout-mount");
  const dispatch = useDispatch();
  // @ts-ignore
  const { isMenuOpen, isSmallScreen } = useSelector((state) => state.dbUi);
  // Assume loading is true on first render
  const { isLoading: isLoadingJobs } = useAppSelector((state) => state.jobs);
  const { isLoading: isLoadingBranches } = useAppSelector(
    (state) => state.branches
  );
  const { isInitialLoading: isLoadingCollaborators } = useAppSelector(
    (state) => state.collaborator
  );
  const { isLastCollaboratorAttendanceRecordLoading } = useAppSelector(
    (state) => state.attendanceRecordsReducer
  );

  const isASmallScreen = useMediaQuery({ query: "(max-width: 900px)" });

  // @ts-ignore
  const { menuState } = useSelector((state: RootState) => state.dbUi);

  // State to track if the initial loading process has started
  const [loadingStarted, setLoadingStarted] = useState(false);
  // State to control rendering based on the loading process
  const [allowRender, setAllowRender] = useState(false);
  // Control para saber si ya cerramos el menú en móvil
  const [hasClosedMobileMenu, setHasClosedMobileMenu] = useState(false);

  useEffect(() => {
    dispatch(setSmallScreen(isASmallScreen));

    // Cerrar el menú en móvil al cargar (solo la primera vez)
    if (isASmallScreen && !hasClosedMobileMenu && isMenuOpen) {
      dispatch(setMenuState(false));
      setTimeout(() => setHasClosedMobileMenu(true), 100);
    }
  }, [isASmallScreen, isMenuOpen, menuState, hasClosedMobileMenu, dispatch]);

  // set the view according both values
  useEffect(() => {
    dispatch(setMenuView(isMenuOpen, isSmallScreen));
  }, [isMenuOpen, isSmallScreen, dispatch]);

  useEffect(() => {
    dispatch(collaboratorThunks.fetchItems());
    dispatch(jobThunks.fetchItems());
    dispatch(branchThunks.fetchItems());
    dispatch(EmploymentThunks.fetchItems({ isActive: true }));
    dispatch(attendanceRecordsThunks.fetchLastRecord());
    setLoadingStarted(true); // Mark that loading has started
  }, [dispatch]);

  useEffect(() => {
    if (
      loadingStarted &&
      !isLoadingCollaborators &&
      !isLoadingJobs &&
      !isLoadingBranches &&
      !isLastCollaboratorAttendanceRecordLoading
    ) {
      perfMark("dashboard-data-loaded");
      setAllowRender(true); // Allow rendering only after loading has completed
    }
  }, [
    isLoadingCollaborators,
    loadingStarted,
    isLoadingJobs,
    isLoadingBranches,
    isLastCollaboratorAttendanceRecordLoading,
  ]);

  if (!allowRender) {
    return <CircularProgress />;
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100vh", // Full viewport height
      }}
    >
      {/* Topbar */}
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 1000,
          backgroundColor: "white", // o el color que uses
        }}
      >
        <Topbar />
      </Box>

      {/* Main Content Area */}
      <Box sx={{ display: "flex", width: "100%", flex: 1 }}>
        {/* Sidebar - Desktop (fixed sidebar) */}
        {!isSmallScreen && menuState !== "close" && (
          <Box
            sx={{
              backgroundColor: SIDEBAR_BG,
              padding: 1,
              width: menuState === "open" ? "200px" : "80px",
              flexShrink: 0,
              height: "calc(100vh - 64px)",
              overflowY: "auto",
            }}
          >
            <Sidebar />
          </Box>
        )}

        {/* Sidebar - Mobile (drawer) */}
        {isSmallScreen && (
          <Drawer
            anchor="left"
            open={hasClosedMobileMenu ? menuState === "open" : false}
            onClose={() => dispatch(setMenuState(false))}
            sx={{
              '& .MuiDrawer-paper': {
                width: 280,
                backgroundColor: SIDEBAR_BG,
                padding: 1,
                paddingTop: 8,
              },
            }}
          >
            <Sidebar />
          </Drawer>
        )}

        {/* Dashboard Main */}
        <Box
          data-dashboard-scroll
          sx={{
            flexGrow: 1,
            overflow: "auto",
            padding: isSmallScreen ? "1rem" : "2rem",
            backgroundColor: "#ebe5e5",
            height: "calc(100vh - 64px)",
          }}
        >
          <ChunkErrorBoundary>
            <Suspense fallback={<CircularProgress />}>
              <DashboardRouter />
            </Suspense>
          </ChunkErrorBoundary>
        </Box>
      </Box>
    </Box>
  );
};
